import React, {useState, useContext} from "react";
import CloseIcon from "./CloseIcon";
import {PageHeading} from "../ui-components/Container";
import {FormattedMessage, useIntl} from "react-intl";
import Input from "../ui-components/Input";
import Textarea from "../ui-components/Textarea";
import Button from "../ui-components/Button";
import {TrashIcon, CheckIcon, PlusIcon, PencilIcon} from "@heroicons/react/24/solid";
import Label from "../ui-components/Label";
import Toggle from "../ui-components/Toggle";
import useDispatchServiceChecklist from "../hooks/useDispatchServiceChecklist";
import {NotificationsContext} from "../ui-components/Notifications";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import WorksiteTaskActivity from "./WorksiteTask/WorksiteTaskActivity";
import {EllipsisVerticalIcon} from "@heroicons/react/24/outline";
import ServiceConfiguratorBlockExpressionConfigurator from "./ServiceConfiguratorBlockExpressionConfigurator";
import {isValidRecursiveQuestion} from "../utils/blockValidation";
import CountryContext from "../CountryContext";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";



const ServiceChecklistEditor = ({ service, selectedChecklist, setSelectedChecklist, setEdits, data }) => {

  const serviceId = service?.id

  // context
  const { push } = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const dispatchChecklist = useDispatchServiceChecklist(serviceId);
  const countryApiRequest = useCountryAxiosInstance(country?.code);

  // state
  const [localChecklist, setLocalChecklist] = useState(selectedChecklist);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDependencies, setShowDependencies] = useState(false);

  // utilities
  const DependenciesIcon = localChecklist.dependencies?.length > 0 ? PencilIcon : PlusIcon;
  const updateLocal = (field, value) => {
    setLocalChecklist((prev) => ({...prev, [field]: value}));
  }

  // callbacks
  const saveAndExit = async () => {
    setSubmitting(true);
    try {
      await dispatchChecklist(selectedChecklist, localChecklist);
      push({type: 'success', title: intl.formatMessage({id: 'checklist_edited_successfully'})});
      setEdits((prev) => prev + 1);
      setSelectedChecklist(null);
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setSubmitting(false);
    }
  }
  const deleteChecklist = async () => {
    setDeleting(true);
    try {
      await countryApiRequest.delete(`/services_checklists/${selectedChecklist.id}`);
      push({type: 'info', title: intl.formatMessage({id: 'checklist_deleted_successfully'})});
      setEdits((prev) => prev + 1);
      setSelectedChecklist(null);
    }
    catch (error)  {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setDeleting(false);
    }
  };
  const handleActivityDrop = (dropped) => {
    // ignore drop outside droppable container
    if (!dropped.destination || !localChecklist?.items.length)
      return;
    // remove dragged item
    const updatedList = [...localChecklist.items]; // copy original
    const [reorderedItem] = updatedList.splice(dropped.source.index, 1);
    // add dropped item
    updatedList.splice(dropped.destination.index, 0, reorderedItem);
    // update state
    updateLocal('items', updatedList.map((a, i) => ({ ...a, val_order: i })));
  };


  if (showDependencies)
    return <ServiceConfiguratorBlockExpressionConfigurator
      setShowDependencies={setShowDependencies}
      fullExpression={localChecklist.dependencies ?? []}
      newData={localChecklist}
      setNewData={setLocalChecklist}
      questions={data.filter((d) => d.type === "question" && !!d.input_type)}
      recursiveQuestions={data.filter(isValidRecursiveQuestion)}
      outputs={data.filter((d) =>
        d.type === "output"
        && ["calculation", "json_output_value"].includes(d.output_type)
      )}
      data={data}
      type="dependencies"
      lockMissingSelectedOptions
      {...service ? {service: service} : {}}
      block={localChecklist}
    />

  return <div className="flex flex-col gap-5 justify-between min-h-full">
    <div>
      <PageHeading
        contentActions={<CloseIcon onClick={() => { setSelectedChecklist(null); }} />}
      />
      <div className="flex flex-col gap-2">


        <Input
          label={intl.formatMessage({id: 'title'})}
          value={localChecklist.des_title}
          onChange={(e) =>  { updateLocal('des_title', e.target.value); }}
          errorMessage={intl.formatMessage({id: 'required_field'})}
          required
          validateOnMount={true}
        />


        <Textarea
          label={intl.formatMessage({id: 'description'})}
          rows={3}
          value={localChecklist.des_description}
          onChange={(e) => { updateLocal('des_description', e.target.value); }}
          errorMessage={intl.formatMessage({id: 'required_field'})}
          hideBottomMargin={true}
          textCls="text-sm"
          required
        />


        <div className="flex justify-between items-center mt-4 mb-5">
          <Label><FormattedMessage id="dependencies" /></Label>
          <DependenciesIcon
            className="text-gray-400 w-8 cursor-pointer"
            onClick={() => { setShowDependencies(true); }}
          />
        </div>


        <Label><FormattedMessage id="activities" /> ({localChecklist.items.length})</Label>
        <DragDropContext onDragEnd={handleActivityDrop}>
          <Droppable droppableId="activities-drop-area">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {localChecklist.items?.map((a, i) => {
                    const key = [i, a.id || "", a.des_title, a.des_description].join("_");
                    return (
                      <Draggable
                        key={key}
                        draggableId={`activities-droppable-${i}`}
                        index={i}
                      >
                        {(provided) => (
                          <div
                            className="mt-1 mb-3 transition-opacity duration-400"
                            key={key}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <WorksiteTaskActivity
                              activityId={a.id}
                              title={a.des_title}
                              description={a.des_description}
                              isDraft={true}
                              editable={true}
                              skippable={false}
                              onCheck={null}
                              onEditComplete={({title, description}) => {
                                updateLocal('items', localChecklist.items.map((pi, idx) => idx !== i ? pi : {
                                  ...pi,
                                  des_title: title,
                                  des_description: description,
                                }));
                              }}
                              onDelete={() => {
                                updateLocal('items', localChecklist.items.filter((_, idx) => idx !== i));
                              }}
                              confirmDelete={false}
                            >
                              <div
                                className="flex items-center font-bold h-10 -mt-5 bg-gray-200 text-gray-500 py-1 px-0 rounded-xl absolute -left-2"
                                {...provided.dragHandleProps}
                              >
                                <EllipsisVerticalIcon className="h-4" />
                              </div>
                            </WorksiteTaskActivity>
                          </div>
                        )}
                      </Draggable>
                    );
                  }
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div className="mb-5">
          <Button
              full
              onClick={(e) => {
                e.preventDefault();
                const newItem = {
                  id_checklist: localChecklist.id,
                  des_title: intl.formatMessage({id: 'new_checklist_activity_title'}),
                  des_description: intl.formatMessage({id: 'new_checklist_activity_description'}),
                  val_order: Math.max(...localChecklist.items.map((i) => i.val_order), 0),
                }
                setLocalChecklist((prev) => ({...prev, items: [...prev.items, newItem]}))
              }}
            >
              <PlusIcon className="w-4" />
              <FormattedMessage id="add_checklist_activity" />
            </Button>
        </div>


        <Toggle
          label={intl.formatMessage({id: 'active'})}
          checked={localChecklist.flg_active}
          onChange={(status) => { updateLocal('flg_active', status); }}
          marginCls=""
        />


      </div>
    </div>
    <div className="flex items-center justify-between mt-2">
      <Button
        styleType="danger"
        onClick={async () => { await deleteChecklist(); }}
        submitting={deleting}
      >
        <TrashIcon className="w-4" />
        <FormattedMessage id="delete" />
      </Button>
      <Button
        onClick={async () => { await saveAndExit(); }}
        submitting={submitting}
      >
        <CheckIcon className="w-4" />
        <FormattedMessage id="save" />
      </Button>
    </div>
  </div>
}

export default ServiceChecklistEditor;